import { Button, Divider, TextField, Grid, Box, Autocomplete, Typography, Alert } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Swal from 'sweetalert2';
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { updateUser } from "../../services/userService";
import PhoneInput from "../PhoneInput/PhoneInput";
import CountryAutocomplete from "../CountryAutocomplete/CountryAutocomplete";
import {
	getPhoneMetaForCountry,
	normalizePhoneForCountry,
	resolvePhoneState,
	validateInternationalPhone,
} from "../../utilities/phoneUtils";
import { COUNTRIES_BY_REGION, REGION_OPTIONS } from "../../data/countryRegions";

const ProfileForm = ({ isEditing, setIsEditing }) => {
    const { t } = useTranslation();
    const { user: currentUser, updateUserStatus } = useAuth();

    const [profileFormData, setProfileFormData] = useState({
        user_first_name: "",
        user_last_name: "",
        user_email: "",
        user_role: "",
        user_display_name: "",
        user_phone: "",
		phone_country_code: "1",
		phone_country_iso2: "US",
		phone_country_flag: "https://flagcdn.com/w40/us.png",
        company_name: "",
        region: "",
        country: "",
    });

    // Email validation state
    const [emailError, setEmailError] = useState(false);
    const [emailHelperText, setEmailHelperText] = useState("");
    const [formErrors, setFormErrors] = useState({});
    const [submissionError, setSubmissionError] = useState("");

    // Comprehensive email validation regex pattern
    const validateEmail = useCallback((email) => {
        // RFC 5322 compliant regex pattern for email validation
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        if (!email.trim()) {
            return { isValid: false, message: t('ALERT_MESSAGES.EMAIL_IS_REQUIRED') };
        }

        if (email.length > 254) {
            return { isValid: false, message: t('GENERAL.EMAIL_ADDRESS_TOO_LONG') };
        }

        if (!emailRegex.test(email.toLowerCase())) {
            return { isValid: false, message: t('GENERAL.PLEASE_ENTER_A_VALID_EMAIL_ADDRESS') };
        }

        // Additional checks for common issues
        if (email.includes('..')) {
            return { isValid: false, message: t('GENERAL.EMAIL_CANNOT_CONTAIN_CONSECUTIVE_DOTS') };
        }

        if (email.startsWith('.') || email.endsWith('.')) {
            return { isValid: false, message: t('GENERAL.EMAIL_CANNOT_START_OR_END_WITH_A_DOT') };
        }

        return { isValid: true, message: "" };
    }, [t]);

    const handleProfileInputChange = (e) => {
        const { name, value } = e.target;

        setProfileFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
        if (submissionError) setSubmissionError("");

        // Real-time email validation
        if (name === 'user_email') {
            const validation = validateEmail(value);
            setEmailError(!validation.isValid);
            setEmailHelperText(validation.message);
        }
    };

    // Handle form submit with validation
    const handleProfileFormSubmit = async () => {
        setSubmissionError("");
        setFormErrors({});

        if (!currentUser?.user_id) {
            setSubmissionError('User not found. Please try logging in again.');
            return;
        }

        // Final email validation before submission
        const emailValidation = validateEmail(profileFormData.user_email);
        if (!emailValidation.isValid) {
            setEmailError(true);
            setEmailHelperText(emailValidation.message);
            setSubmissionError(emailValidation.message);
            return;
        }

        const nextErrors = {};

        if (!profileFormData.user_first_name.trim()) {
            nextErrors.user_first_name = t('UI_TEXT.FIRST_NAME_IS_REQUIRED');
        }

        if (!profileFormData.user_last_name.trim()) {
            nextErrors.user_last_name = t('UI_TEXT.LAST_NAME_IS_REQUIRED');
        }

        const user_id = currentUser.user_id;
        const phoneCheck = validateInternationalPhone(profileFormData.user_phone);
        if (!phoneCheck.ok) {
            if (phoneCheck.reason === "too_short") nextErrors.user_phone = t('GENERAL.PHONE_NUMBER_IS_TOO_SHORT');
            else if (phoneCheck.reason === "too_long") nextErrors.user_phone = t('GENERAL.PHONE_NUMBER_IS_TOO_LONG');
            else nextErrors.user_phone = t('GENERAL.PHONE_NUMBER_IS_REQUIRED_DOT');
        }

        if (Object.keys(nextErrors).length > 0) {
            setFormErrors(nextErrors);
            setSubmissionError(t('UI_TEXT.PLEASE_FIX_THE_HIGHLIGHTED_FIELDS'));
            return;
        }

		const phoneMeta = getPhoneMetaForCountry(profileFormData.phone_country_iso2 || "US");
        const computedDisplayName = `${profileFormData.user_first_name} ${profileFormData.user_last_name}`.trim();
        const user_meta = {
            company_name: profileFormData.company_name || '',
            region: profileFormData.region || '',
            country: profileFormData.country || '',
            phone_country_code: phoneMeta.phone_country_code,
            phone_country_iso2: phoneMeta.phone_country_iso2,
            phone_country_flag: phoneMeta.phone_country_flag,
        };

        const req_body = {
            userId: user_id,
            mode: "profile",
            data: {
                user_first_name: profileFormData.user_first_name,
                user_last_name: profileFormData.user_last_name,
                user_email: profileFormData.user_email,
                user_display_name: computedDisplayName || profileFormData.user_display_name,
                user_phone: profileFormData.user_phone,
                user_meta,
            },
        };

        try {
            const response = await updateUser(req_body);
            const ok = response?.status === true || response?.success === true;
            if (ok) {
                const { user_role: _omittedRole, ...profileWithoutRole } = profileFormData;
                const contextUpdate = {
                    ...profileWithoutRole,
                    user_display_name: computedDisplayName || profileFormData.user_display_name,
                    ...user_meta,
					user_meta,
                };
                updateUserStatus(contextUpdate);
                setProfileFormData((prev) => ({ ...prev, user_display_name: computedDisplayName || prev.user_display_name }));
                setIsEditing(false);
                setFormErrors({});
                setSubmissionError("");

                Swal.fire({
                    icon: 'success',
                    title: t('TITLES.PROFILE_UPDATED'),
                    text: t('UI_TEXT.PROFILE_UPDATED_SUCCESSFULLY'),
                });
            } else {
                throw new Error(response?.message || `Update failed with status: ${response?.status}`);
            }
        } catch (error) {
            console.error("Error updating profile:", error);

            // Handle specific error cases
            if (error.response?.status === 409) {
                Swal.fire({
                    icon: 'error',
                    title: t('ALERT_MESSAGES.EMAIL_ALREADY_EXISTS'),
                    text: t('ALERT_MESSAGES.THIS_EMAIL_ADDRESS_IS_ALREADY_ASSOCIATED_WITH_ANOTHER_ACCOUNT'),
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: error.message || 'Failed to update profile. Please try again.',
                });
            }
        }
    };

    // Initialize form data when currentUser changes
    useEffect(() => {
        if (currentUser && currentUser.user_first_name) {
			const phoneState = resolvePhoneState({
				phoneValue: currentUser.user_phone || "",
				countryLabel: currentUser.country || currentUser?.user_meta?.country || "",
				phoneCountryIso2: currentUser.phone_country_iso2 || currentUser?.user_meta?.phone_country_iso2 || "",
				phoneCountryCode: currentUser.phone_country_code || currentUser?.user_meta?.phone_country_code || "",
			});
            setProfileFormData({
                user_first_name: currentUser.user_first_name || "",
                user_last_name: currentUser.user_last_name || "",
                user_email: currentUser.user_email || "",
                user_role: currentUser.role?.role_name || "",
                user_display_name: currentUser.user_display_name || "",
                user_phone: phoneState.phoneValue,
				phone_country_code: phoneState.meta.phone_country_code,
				phone_country_iso2: phoneState.meta.phone_country_iso2,
				phone_country_flag: phoneState.meta.phone_country_flag,
                company_name: currentUser.company_name || currentUser?.user_meta?.company_name || "",
                region: currentUser.region || currentUser?.user_meta?.region || "",
                country: currentUser.country || currentUser?.user_meta?.country || "",
                company_street_address: currentUser?.company_street_address || currentUser?.user_meta?.company_street_address || "",
                company_city: currentUser?.company_city || currentUser?.user_meta?.company_city || "",
                company_zip_postal_code: currentUser?.company_zip_postal_code || currentUser?.user_meta?.company_zip_postal_code || "",
            });


            // Reset email validation when user data loads
            if (currentUser.user_email) {
                const emailValidation = validateEmail(currentUser.user_email);
                setEmailError(!emailValidation.isValid);
                setEmailHelperText(emailValidation.message);
            }
        }
    }, [currentUser, validateEmail]);

    return (
        <div className="profile-img-form-section">
            {/* <div className="img-section">
                <img src="/user-avatar.jpg" alt="user-img" />
            </div>
            <Divider flexItem orientation="vertical" sx={{ my: 0.5 }} /> */}
            <div className="form-section">
                {submissionError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submissionError}
                    </Alert>
                )}
                <div className="form-group">
                    <TextField
                        label={t('LABELS.FIRST_NAME')}
                        name="user_first_name"
                        value={profileFormData.user_first_name}
                        fullWidth
                        size="small"
                        disabled={!isEditing}
                        onChange={handleProfileInputChange}
                        className="form-input"
                        required
                        inputProps={{ maxLength: 50 }}
                        error={Boolean(formErrors.user_first_name)}
                        helperText={formErrors.user_first_name || ""}
                    />
                    <TextField
                        label={t('LABELS.LAST_NAME')}
                        name="user_last_name"
                        value={profileFormData.user_last_name}
                        fullWidth
                        size="small"
                        disabled={!isEditing}
                        onChange={handleProfileInputChange}
                        className="form-input"
                        required
                        inputProps={{ maxLength: 50 }}
                        error={Boolean(formErrors.user_last_name)}
                        helperText={formErrors.user_last_name || ""}
                    />
                </div>

                <Box >
					<PhoneInput
						value={`${profileFormData.user_phone}`}
						countryIso2={profileFormData.phone_country_iso2}
						countryCode={profileFormData.phone_country_code}
						countryLabel={profileFormData.country}
						onChange={(value) => {
							setProfileFormData((prev) => ({
								...prev,
								user_phone: value,
							}));
							setFormErrors((prev) => ({ ...prev, user_phone: "" }));
							if (submissionError) setSubmissionError("");
						}}
						onCountryChange={(country) => {
							const phoneMeta = getPhoneMetaForCountry(country);
							setProfileFormData((prev) => ({
								...prev,
								user_phone: normalizePhoneForCountry(prev.user_phone || "", country),
								...phoneMeta,
							}));
						}}
						label={t('LABELS.PHONE')}
						allowCountryChange={isEditing}
						disabled={!isEditing}
						error={Boolean(formErrors.user_phone)}
						helperText={formErrors.user_phone || ""}
					/>
                </Box>
                <TextField
                    label={t('LABELS.EMAIL')}
                    name="user_email"
                    type="email"
                    value={profileFormData.user_email}
                    fullWidth
                    size="small"
                    disabled
                    onChange={handleProfileInputChange}
                    className="form-input"
                    required
                    error={emailError}
                    helperText={emailHelperText}
                    inputProps={{
                        maxLength: 254,
                        autoComplete: 'email'
                    }}
                    // Additional HTML5 validation
                    onBlur={(e) => {
                        if (isEditing && e.target.value) {
                            const validation = validateEmail(e.target.value);
                            setEmailError(!validation.isValid);
                            setEmailHelperText(validation.message);
                        }
                    }}
                />
                <TextField
                    label={t('LABELS.ROLE')}
                    value={profileFormData.user_role}
                    fullWidth
                    size="small"
                    disabled
                    className="form-input"
                />
                {isEditing && <Typography variant="body2" color="text.secondary"><b>Note: </b> {t('USER_MANAGEMENT.PLEASE_CONTACT_THE_CCR_TO_UPDATE_THE_EMAIL_OR_ROLE')}</Typography>}
                {/* Company Details */}
                <Divider sx={{ my: 1 }}>{t('TITLES.COMPANY_DETAILS')}</Divider>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('LABELS.COMPANY_NAME')}
                            name="company_name"
                            value={profileFormData.company_name}
                            fullWidth
                            size="small"
                            disabled={!isEditing}
                            onChange={handleProfileInputChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            options={REGION_OPTIONS}
                            value={profileFormData.region || null}
                            onChange={(_, v) => setProfileFormData(prev => ({ ...prev, region: v || '', country: v ? prev.country : '' }))}
                            autoHighlight
                            selectOnFocus
                            handleHomeEndKeys
                            openOnFocus
                            renderInput={(params) => (
                                <TextField {...params} label={t('LABELS.REGION')} size="small" />
                            )}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <CountryAutocomplete
                            options={COUNTRIES_BY_REGION[profileFormData.region] || []}
                            value={profileFormData.country || ""}
                            onChange={(nextCountry) => setProfileFormData(prev => ({ ...prev, country: nextCountry || "" }))}
                            label={t('LABELS.COUNTRY')}
                            size="small"
                            margin="none"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label={t('LABELS.STREET_ADDRESS')}
                            name="company_street_address"
                            value={profileFormData.company_street_address || ''}
                            fullWidth
                            size="small"
                            disabled={!isEditing}
                            onChange={(e) => setProfileFormData(prev => ({ ...prev, company_street_address: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={t('LABELS.CITY')}
                            name="company_city"
                            value={profileFormData.company_city || ''}
                            fullWidth
                            size="small"
                            disabled={!isEditing}
                            onChange={(e) => setProfileFormData(prev => ({ ...prev, company_city: e.target.value }))}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label={t('LABELS.ZIP_POSTAL_CODE')}
                            name="company_zip_postal_code"
                            value={profileFormData.company_zip_postal_code || ''}
                            fullWidth
                            size="small"
                            disabled={!isEditing}
                            onChange={(e) => setProfileFormData(prev => ({ ...prev, company_zip_postal_code: e.target.value }))}
                        />
                    </Grid>
                </Grid>
                {isEditing && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', columnGap: 1 }}>
                        <Button variant="outlined" onClick={() => setIsEditing(false)}>{t('BUTTON.CANCEL')}</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={emailError}
                            className="button-primary"
                            onClick={handleProfileFormSubmit}>
                            {t('BUTTON.UPDATE_PROFILE')}
                        </Button>
                    </Box>
                )}
            </div>
        </div>
    );
};

export default ProfileForm;
